<template>
  <div>
    <div class="mb-4 flex flex-col">
      <label>offer for customer</label>
      <input
        type="text"
        class="item-name-input"
        :class="{ error: !bonusPointsDraftState.item_name }"
        v-model="bonusPointsDraftState.item_name"
        maxlength="50"
        data-cy="bonus-points-name-input"
      />
      <span class="form-field-notice">max 50 characters</span>
    </div>

    <div class="mb-4 flex flex-col">
      <label>bonus type</label>
      <div class="tab-container" :class="{ error: !bonusPointsDraftState.bonus_type }">
        <div @click="bonusPointsDraftState.bonus_type = 'add'"
              class="tab"
              :class="{ selected: bonusPointsDraftState.bonus_type === 'add', error: !bonusPointsDraftState.bonus_type }"
              data-cy="bonus-points-add-button"
        >
          <span>+</span>
          <span>add</span>
        </div>

        <div @click="bonusPointsDraftState.bonus_type = 'multiply'"
              class="tab"
              :class="{ selected: bonusPointsDraftState.bonus_type === 'multiply', error: !bonusPointsDraftState.bonus_type }"
              data-cy="bonus-points-multiply-button"
        >
          <span>x</span>
          <span>multiply</span>
        </div>
      </div>
    </div>

    <div class="mb-4 flex flex-col">
      <label>bonus value</label>
      <input
        type="number"
        class="item-name-input"
        :class="{ error: isBonusValueEmptyOrLessThan1 }"
        v-model.number="bonusPointsDraftState.bonus_value"
        min="0"
        step="1"
        data-cy="bonus-points-bonus-value-input"
      />
    </div>

    <div class="mb-4 flex flex-col">
      <label>offers per customer</label>
      <select
        v-model="bonusPointsDraftState.max_redemptions"
        data-cy="bonus-points-max-redemptions-select"
      >
        <option value="1">1</option>
        <option value="2">2</option>
        <option value="3">3</option>
        <option value="4">4</option>
        <option value="5">5</option>
        <option value="10">10</option>
        <option value="-1">unlimited</option>
      </select>
    </div>

    <div class="mb-4 flex flex-col">
      <label>expiration date</label>
      <span class="form-field-notice">expires at 11:59 PM</span>
      <sb-date
        :flatpickr="flatPickrConfig"
        :include="['date']"
        placeholder="-"
        format="E, MMM d, y"
        mode="single"
        :value="expirationDate.value"
        :class="{ error: !bonusPointsDraftState.expiration_date}"
        class="expiration-date"
        @update:value="changedExpiration"
        data-cy="bonus-points-expiration-date-input" />
      <span v-if="expirationDate.error" class="form-field-notice error">{{ expirationDate.error }}</span>
    </div>

    <div class="mb-4 flex flex-col">
      <label>min purchase amount</label>
      <input
        type="number"
        class="min-purchase"
        :class="{ error: !bonusPointsDraftState.min_purchase_amount }"
        v-model="bonusPointsDraftState.min_purchase_amount"
        placeholder="$0.00"
        min="0"
        step="0.1"
        data-cy="bonus-points-min-purchase-amount-input"
      />
    </div>

    <div class="mb-4 flex flex-col">
      <label>display image</label>
      <span class="form-field-notice">displays on offers page</span>
      <span v-if="displayImage.error" class="form-field-notice error">{{ displayImage.error }}</span>
      <sb-loading-icon v-if="displayImage.loading" />
      <template v-else-if="displayImage.file">
          <img :src="displayImage.file.thumb" alt="display image" class="w-max" />
        <span class="remove-btn" @click="changedDisplayImage()" data-cy="bonus-points-display-image-remove-button">remove</span>
      </template>
      <template v-else>
        <a
          v-file-upload:png,gif,jpeg="changedDisplayImage"
          class="mb-1"
          data-cy="bonus-points-display-image-button">
          <fai :icon="['fas', 'camera']" />
        </a>
      </template>
    </div>

    <div class="mb-4 flex items-center">
      <checkbox
        :checked.sync="bonusPointsDraftState.apply_to_non_opt_in"
        class="h-5 w-5 flex-none"
        id="applyToNonOptIn"
      />
      <label class="leading-4 pl-1.5" for="applyToNonOptIn">
        issue bonus point offer to members who would qualify for this message but are not opted in (member offer)
      </label>
    </div>
  </div>
</template>

<script>
import { attachmentAPI } from '../../../../services/api/attachmentAPI.js';
import { isBefore, add } from "date-fns";
import { mapGetters } from "vuex";
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";
import { Checkbox } from "@springbig/sb-components";

export default {
  name: "bonus-points-form",

  components: {Checkbox},

  props: {
    bonusPointsDraftState: Object,
    campaign: Object,
  },

  data() {
    return {
      displayImage: {
        file: null,
        loading: false,
        error: null
      },
      error: null,
      expirationDate: {
        value: null,
        error: null
      },
    }
  },

  created() {
    this.bonusPointsDraftState.type = "BonusReward";
    this.expirationDate.value = !!this.bonusPointsDraftState.expiration_date ? utcToZonedTime(this.bonusPointsDraftState.expiration_date, this.merchantTimeZone) : null;
    this.refreshAttachments();
  },

  methods: {
    async changedDisplayImage(file) {
      this.displayImage.loading = true;
      this.displayImage.error = null;

      try {
        if(!file && this.displayImage.file) {
          await attachmentAPI.delete(this.displayImage.file.uuid);
          this.bonusPointsDraftState.has_preload_image = false;
        } else {
          await attachmentAPI.create(this.bonusPointsDraftState.id, 'preload', 'Reward', file);
          this.bonusPointsDraftState.has_preload_image = true;
        }
      } catch(e) {
        // TODO: Handle error here
        this.error = e;
      }

      await this.refreshAttachments();
      this.displayImage.loading = false;
    },

    changedExpiration(selectedDate) {
      this.expirationDate.value = selectedDate;
      this.expirationDate.error = this.validateExpirationDate(selectedDate);

      const formattedDate = selectedDate.length === 0 ? null : zonedTimeToUtc(selectedDate, this.merchantTimeZone).toJSON();
      this.bonusPointsDraftState.expiration_date = this.expirationDate.error ? null : formattedDate;
      this.bonusPointsDraftState.expiration_date_local = this.expirationDate.error ? null :formattedDate
    },

    async refreshAttachments() {
      this.displayImage.file = null;

      if(this.bonusPointsDraftState.id !== null) {
        const attachments = await attachmentAPI.forReward(this.bonusPointsDraftState.id, this.campaign.id, "Campaign")

        if(Array.isArray(attachments)) {
          const preloads = attachments.filter((attachment) => attachment.label === "preload");
          if(preloads.length > 0) {
            this.displayImage.file = preloads[preloads.length - 1];
            this.bonusPointsDraftState.has_preload_image = true;
          }
        }
      }
    },

    validateExpirationDate(selectedDate){
      if(
        this.campaign.send_at_local &&
        isBefore(
          add(selectedDate, {days: 1}),
          new Date(this.campaign.send_at_local)
        )
      ){
        return "can't expire before campaign send time"
      }
      return null
    },
  },

  computed: {
    flatPickrConfig() {
      return {
        minDate: new Date()
      }
    },
    isBonusValueEmptyOrLessThan1(){
      return !this.bonusPointsDraftState.bonus_value || this.bonusPointsDraftState.bonus_value < 1;
    },
    ...mapGetters(["merchantTimeZone"])
  }
}
</script>

<style scoped>
.sb-date >>> .label-container {
  padding: 8px;
}
</style>
